/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryCache } from 'react-query';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import syntaxTheme from 'react-syntax-highlighter/dist/esm/styles/hljs/github-gist';
import {
  AutoSizer,
  Column,
  ColumnProps,
  defaultTableCellDataGetter,
  defaultTableCellRenderer,
  defaultTableHeaderRenderer,
  Table,
} from 'react-virtualized';

import CopyIcon from '@mui/icons-material/ContentCopy';
import NoAdviceIcon from '@mui/icons-material/Inbox';
import HideIcon from '@mui/icons-material/VisibilityOff';
import { ButtonGroup, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import { AdviceSeverity, AdviceSeverityColor } from 'shared';

import { CopyToClipboard } from '../../../../../components/CopyToClipboard/component';
import { notifyReportableApiError } from '../../../../../components/notifyReportableApiError';
import { ClusterApiService } from '../../../../../services/ClusterApiService';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { fetchAdvice, fetchDismissedAdvice } from '../../../../../utils/queryFns';
import { Advice } from '../AdviceTab.types';
import { AdviceInfo } from './AdviceInfo';

SyntaxHighlighter.registerLanguage('sql', sql);

const columnDefaults: (typeof Column)['defaultProps'] = {
  cellDataGetter: defaultTableCellDataGetter,
  cellRenderer: defaultTableCellRenderer,
  flexGrow: 0,
  flexShrink: 1,
  headerRenderer: defaultTableHeaderRenderer,
  style: {},
};

const createColumn = (props: ColumnProps) => {
  return <Column {...columnDefaults} {...props} />;
};

type Props = {
  identificationData: UpdateClusterDetailsParameterIdentification;
  advice: Advice[];
};

export const AdviceTable = memo<Props>(({ identificationData, advice }) => {
  const { t } = useTranslation();

  const [adviceInfo, setAdviceInfo] = useState<Props['advice'][number] | null>(null);
  const [openAdvice, setOpenAdvice] = useState(false);

  const openAdviceInfo = (advice: Props['advice'][number]) => {
    setAdviceInfo(advice);
    setOpenAdvice(true);
  };

  const closeAdviceInfo = () => {
    setOpenAdvice(false);
  };

  const dismissAdvice = useCallback(
    (adv: Advice) => {
      ClusterApiService.dismissAdvice(
        {
          organizationId: identificationData.organizationId,
          projectId: identificationData.projectId,
          clusterId: identificationData.clusterId,
        },
        adv,
      )
        .then(() =>
          Promise.all([
            queryCache.invalidateQueries([fetchAdvice.key, { ...identificationData }]),
            queryCache.invalidateQueries([fetchDismissedAdvice.key, { ...identificationData }]),
          ]),
        )
        .catch(notifyReportableApiError);
    },
    [identificationData],
  );

  return (
    <AutoSizer>
      {({ width, height }) => (
        <>
          <Table
            width={width}
            height={height}
            headerHeight={20}
            rowHeight={40}
            rowCount={advice.length}
            rowGetter={({ index }) => advice[index]}
            noRowsRenderer={() => (
              <Stack alignItems="center" m={2}>
                <NoAdviceIcon />
                <Typography>{t('cluster:advice.table.noAdvice')}</Typography>
              </Stack>
            )}
            overscanRowCount={5}
            rowStyle={{ borderBottom: '0.5px solid #ccc', fontSize: 12, cursor: 'pointer' }}
            onRowClick={e => openAdviceInfo(e.rowData)}
          >
            {createColumn({
              dataKey: 'severity',
              width: 18,
              cellRenderer: datum => (
                <Tooltip title={t(`cluster:advice.severities.${datum.cellData}`)} arrow>
                  <Box
                    style={{
                      backgroundColor: AdviceSeverityColor[datum.cellData as AdviceSeverity],
                      borderRadius: '50%',
                      width: '10px',
                      height: '10px',
                    }}
                  />
                </Tooltip>
              ),
            })}
            {createColumn({
              label: t(`cluster:advice.labels.type`),
              dataKey: 'type',
              width: 90,
              cellRenderer: datum => t(`cluster:advice.modules.${datum.cellData}`),
            })}
            {createColumn({
              label: t(`cluster:advice.labels.object`),
              dataKey: 'object',
              width: width / 5,
              style: { fontFamily: 'monospace' },
              cellRenderer: datum => datum.cellData || `N/A`,
            })}
            {createColumn({
              label: t(`cluster:advice.labels.info`),
              dataKey: 'info',
              width: width / 5,
              flexGrow: 1,
            })}
            {createColumn({
              label: t(`cluster:advice.labels.script`),
              dataKey: 'script',
              width: width / 4,
              cellRenderer: datum =>
                datum.cellData ? (
                  <SyntaxHighlighter language="sql" style={syntaxTheme}>
                    {datum.cellData}
                  </SyntaxHighlighter>
                ) : null,
            })}
            {createColumn({
              label: t(`cluster:advice.table.actions`),
              dataKey: 'script',
              width: 120,
              headerStyle: { textAlign: 'center' },
              style: { display: 'flex', justifyContent: 'center' },
              cellRenderer: datum => (
                <ButtonGroup variant="text">
                  <Tooltip title={t('cluster:advice.table.dismiss')}>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        dismissAdvice(datum.rowData);
                      }}
                    >
                      <HideIcon />
                    </IconButton>
                  </Tooltip>
                  {datum.rowData.script ? (
                    <CopyToClipboard text={datum.rowData.script}>
                      <IconButton>
                        <CopyIcon />
                      </IconButton>
                    </CopyToClipboard>
                  ) : null}
                </ButtonGroup>
              ),
            })}
          </Table>
          <AdviceInfo open={openAdvice} onClose={closeAdviceInfo} advice={adviceInfo} />
        </>
      )}
    </AutoSizer>
  );
});
